import { type FormEvent, useState } from "react";
import { Arrow } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/Sections";
import { useI18n } from "../i18n";

export function ContactPage() {
  const { l, locale } = useI18n();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reference, setReference] = useState("");
  const [topicError, setTopicError] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const topics = data.getAll("topics");
    if (topics.length === 0) {
      setTopicError(true);
      form.querySelector<HTMLInputElement>('input[name="topics"]')?.focus();
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          organization: data.get("organization"),
          topics,
          message: data.get("message"),
          website: data.get("website"),
          locale,
        }),
      });
      const result = (await response.json()) as {
        ok: boolean;
        reference?: string;
        error?: string;
      };
      if (!response.ok || !result.ok) {
        throw new Error(result.error ?? "Submission failed");
      }
      setReference(result.reference ?? "");
      setSubmitted(true);
      form.reset();
    } catch {
      setSubmitError(
        l(
          "暂时无法提交，请稍后重试，或直接发送邮件至 hello@accsci.top。",
          "We could not submit this yet. Try again shortly or email hello@accsci.top.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageMeta title={l("提交想法", "Share your idea")} />
      <PageHero
        eyebrow={l("提交想法", "START A PROJECT")}
        title={
          <>
            {l("先告诉我们你的想法，", "Tell us the idea. ")}
            <span className="blue-text">
              {l("我们让它开始工作。", "We’ll make it work.")}
            </span>
          </>
        }
        lead={l(
          "不需要完整需求文档。描述目标、用户、当前阶段和最想验证的问题，我们会一起判断最快、最现实的下一步。",
          "You do not need a finished specification. Share the goal, user, current stage, and the question you most need to validate—we will find the fastest realistic next step.",
        )}
        aside={
          <div className="contact-signal">
            <i />
            {l(
              "通常在 1–2 个工作日内回复",
              "Usually replies within 1–2 business days",
            )}
            <br />
            <span>
              {l(
                "提交后会发送确认邮件",
                "A confirmation email follows submission",
              )}
            </span>
          </div>
        }
      />
      <section className="contact-section">
        <div className="container contact-grid">
          <aside className="contact-aside">
            <span className="micro-label">
              {l("沟通流程", "WHAT HAPPENS NEXT")}
            </span>
            <ol>
              <li>
                <span>01</span>
                <div>
                  <strong>{l("理解想法", "Understand the idea")}</strong>
                  <p>
                    {l(
                      "目标、用户、现状与约束",
                      "Goal, user, current state, constraints",
                    )}
                  </p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>{l("选择路径", "Choose a path")}</strong>
                  <p>
                    {l(
                      "原型方式与工程边界",
                      "Prototype format and engineering boundary",
                    )}
                  </p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>{l("明确下一步", "Define the next step")}</strong>
                  <p>
                    {l(
                      "范围、节奏与协作方式",
                      "Scope, pace, and way of working",
                    )}
                  </p>
                </div>
              </li>
            </ol>
            <div className="contact-direct">
              <span>{l("直接联系", "DIRECT CONTACT")}</span>
              <a href="mailto:hello@accsci.top">hello@accsci.top</a>
            </div>
          </aside>
          {submitted ? (
            <div className="form-success" role="status" aria-live="polite">
              <span>{l("提交成功", "BRIEF RECEIVED")}</span>
              <h2>{l("我们已经收到你的想法。", "We received your idea.")}</h2>
              <p>
                {l(
                  "项目资料已安全保存，我们通常会在 1–2 个工作日内回复。确认邮件正在发送。",
                  "Your project brief has been stored securely. We usually reply within 1–2 business days, and a confirmation email is on its way.",
                )}
              </p>
              {reference && <small>REF · {reference}</small>}
              <button
                type="button"
                className="button"
                onClick={() => setSubmitted(false)}
              >
                {l("返回表单", "Back to form")} <Arrow />
              </button>
            </div>
          ) : (
            <form
              className="contact-form"
              action="#"
              method="post"
              onSubmit={handleSubmit}
            >
              <div className="contact-honeypot" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="form-row">
                <label>
                  <span>{l("你的称呼 *", "Your name *")}</span>
                  <input
                    name="name"
                    autoComplete="name"
                    placeholder={l("如何称呼你", "How should we address you?")}
                    required
                  />
                </label>
                <label>
                  <span>{l("工作邮箱 *", "Work email *")}</span>
                  <input
                    name="email"
                    type="email"
                    spellCheck={false}
                    autoComplete="email"
                    placeholder="name@company.com"
                    required
                  />
                </label>
              </div>
              <label>
                <span>{l("公司 / 团队", "Company / team")}</span>
                <input
                  name="organization"
                  autoComplete="organization"
                  placeholder={l("你的组织名称", "Your organization")}
                />
              </label>
              <fieldset
                aria-describedby={topicError ? "topic-error" : undefined}
              >
                <legend>
                  {l("你想做什么？ *", "What are you building? *")}
                </legend>
                <div className="choice-grid">
                  {(locale === "zh"
                    ? [
                        "AI 软件 / SaaS",
                        "智能硬件 / 联网设备",
                        "软硬件一体产品",
                        "快速原型",
                        "正式产品研发",
                        "试点 / 发布",
                        "量产 / 运维",
                        "还不确定",
                      ]
                    : [
                        "AI software / SaaS",
                        "Smart hardware / connected device",
                        "Integrated software + hardware",
                        "Rapid prototype",
                        "Product build",
                        "Pilot / launch",
                        "Scale / operations",
                        "Not sure yet",
                      ]
                  ).map((item, index) => (
                    <label key={item} htmlFor={`topic-${index}`}>
                      <input
                        id={`topic-${index}`}
                        type="checkbox"
                        name="topics"
                        value={item}
                        onChange={(event) =>
                          event.currentTarget.checked && setTopicError(false)
                        }
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
                {topicError && (
                  <p
                    id="topic-error"
                    className="field-error"
                    aria-live="polite"
                  >
                    {l("请至少选择一个方向。", "Choose at least one option.")}
                  </p>
                )}
              </fieldset>
              <label>
                <span>{l("项目背景 *", "Project context *")}</span>
                <textarea
                  name="message"
                  rows={6}
                  placeholder={l(
                    "简单介绍目标、用户、当前阶段，以及最想先验证的问题……",
                    "Briefly describe the goal, user, current stage, and the first question you need to validate…",
                  )}
                  required
                />
              </label>
              <div className="form-footer">
                <p>
                  {l(
                    "提交即表示你同意我们仅为项目沟通处理这些信息。",
                    "By submitting, you agree that we may process this information for project communication.",
                  )}
                </p>
                {submitError && (
                  <p className="field-error" role="alert">
                    {submitError}
                  </p>
                )}
                <button className="button" type="submit" disabled={submitting}>
                  {submitting
                    ? l("正在发送…", "Sending…")
                    : l("发送项目信息", "Send project brief")}{" "}
                  <Arrow direction="up-right" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
